import React from "react";
import "./Safety.css";

const TextBox = () => {
  return (
    <div>
      <p>
        <figure>
          <center>
            <img
              src="https://static.igem.wiki/teams/4888/wiki/safe-work-with-cadmium.jpg"
              className="fig1saf"
              alt="wirking safe"
            />
          </center>
        </figure>
      </p>
      <h2 id="safe-environment">
        Working in a safe environment - laboratory, GMO and cadmium
      </h2>
      <hr class="dividergeneral mb-5"></hr>
      <p className="mb-2">
        Working within a laboratory, particularly when dealing with genetically
        modified organisms (GMOs), necessitates stringent safety protocols. This
        was particularely important given that we were working with cadmium, a
        dangerous heavy metal. As such, we incorporated extra precautions
        throughout our experimental, waste disposal, and storage processes.
      </p>
      <p className="mb-2">
        Prior to initiating our work with cadmium, our team undertook
        specialized laboratory training to ensure proficiency in both
        fundamental procedures and safety standards.
      </p>
      <p>
        We consulted with the Occupational Safety and Health (OSH) department at
        EPFL to delineate specific safety protocols. We implemented distinct
        waste management practices for handling cadmium and carried out all
        experiments within a controlled environment, wearing essential
        protective equipments including a fume hood, P3 masks, lab coats, and
        safety goggles.
      </p>
      <figure className="mt-10">
        <center>
          <img
            src="https://static.igem.wiki/teams/4888/wiki/whatsapp-image-2023-10-05-at-10-46-01.jpeg"
            style={{
              paddingTop: "30px",
              paddingBottom: "30px",
              width: "50%",
              height: "auto",
            }}
            alt="Charlotte and Matthis on there way to work with cadmium"
          />
          <i>
            <figcaption className="mt-[-1rem]">
              Charlotte and Matthis are on their way to work with cadmium.
            </figcaption>
          </i>
        </center>
      </figure>
      <p className="mt-10 mb-2">
        During our initial experimental phase, we opted for zinc as a proxy for
        preliminary assessments, given its chemically analogous properties to
        cadmium and its affinity for binding with EC20. This approach enabled us
        to calibrate various experimental parameters, including incubation
        duration, bacterial load, and temperature, thereby establishing optimal
        conditions. Furthermore, it facilitated our proficiency with
        spectrometric methodologies, while concurrently minimizing exposure to
        hazardous substances. In subsequent phases, we expanded our
        investigations to incorporate cadmium, leveraging insights derived from
        our earlier zinc-based experiments to ensure methodological efficiency.
      </p>
      <p className="mb-2">
        We carefully selected our colorimetric test for safety considerations.
        The supplier's cuvettes are specifically engineered to mitigate exposure
        risks, and the responsibility of waste management resides with the
        provider.
      </p>
      <p>
        We have documented all our safety measures for working with cadmium in
        the following document, for subsequent iGEM teams. Unfortunately, the Incidin Pro document is in French, because the company did not provided 
        any infomative document in English. 
      </p>
            
      <center>
        <details className="mt-10 mb-20">
          <summary id="working-with-cadmium">
            <mark>
              <b>Safety - working with cadmium </b>
            </mark>
          </summary>
          <object
            data="https://static.igem.wiki/teams/4888/wiki/contribution/safety-guidelines.pdf"
            width="800"
            height="500"
            aria-label="Working with cadmium"
          ></object>
        </details>
      </center>
      <br />
          <h2 id="Avoiding GMO propagation">
        Avoiding modified bacteria propagation
      </h2>
          <hr className="dividergeneral mb-5"></hr>

          <h3 id="Auxotrophy" className="mt-10">
              Auxotrophy
      </h3>
      <p className="mb-2">
        In order to achieve its intended effects, our genetically modified
        bacteria will come into contact with various aspects of the human body,
        including the digestive tract and the microbiota. Given the genetic
        modification, it is imperative to ensure that these bacteria do not
        disrupt bodily functions, colonize the microbiota, or persist in the
        external environment.
      </p>
      <p className="mb-2">
        To address this concern, we initially conducted all proof of concept
        experiments using the laboratory strain of <i>E. coli</i>. However, the
        ultimate goal is to employ human intestinal <i>E. coli</i> strains or
        <i>Lactobacillus</i> for the final product. These strains are naturally present
        in the intestinal tract, which mitigates the risk of adverse reactions
        from the body and unwanted effects.
      </p>
      <p className="mb-2">
              Furthermore, we plan to utilize auxotrophic strains. In the intestinal
              medium, there are amino acids present, but their concentration is
              insufficient to support bacterial growth. This
              strategic choice ensures that the bacteria can survive their journey
              through the body but will be incapable of growth, division, and proper
              colonization. This approach effectively prevents microbiome colonization
              and disturbance. Moreover, as the bacteria exit the body, their
              inability to access adequate amino acids will ultimately lead to death.
      </p>
          <h3 id="Mucus-binding management" className="mt-10">
              Mucus-binding management
      </h3>
      <p className="mb-2">
        As explained on the <a href="../epfl/engineering"> engineering page</a>,
        we have developed a system that prevents the production of SpaC outside
        the laboratory medium. This control is achieved through the use of IPTG
        and theophylline. After the freeze-drying process of the probiotic, SpaC
        production is halted as the metabolism of the bacteria stop but protein
        would be preserved. Since the half-life of SpaC falls within the range
        of approximately 48 hours, the bacteria will have a limited window of
        time during which they can adhere to the mucus after reactivation
        through hydration. In addition, human mucus undergoes a natural turnover
        process, which serves as an additional mechanism for expelling bacteria
        from the body, even if they are attached to the mucus.
      </p>
          <h3 id="to go" className="mt-10">
              To go further...
      </h3>
      <p className="mb-2">
        This ensures that all genetically modified bacteria are expelled from
        the body and ultimately perish. Our final concern revolves around
        horizontal gene transfer (HGT). Indeed, even if our genetically modified
        bacteria perish, the possibility of gene transfer between microbiotal
        strains and our bacteria before their demise remains a concern. Such
        transfer, involving one of our plasmids, could enable other bacteria to
        bind cadmium, produce SpaC, and develop antibiotic resistance. In the
        case of SpaC, the presence of IPTG and theophylline would still be
        required, making this occurrence less likely. Regarding EC20, it not
        anticipated to confer a particular advantage to the bacteria. However,
        we utilize two different antibiotic in our plasmids : ampicillin
        kanamycin. The primary issue is the development of antibiotic
        resistance.
      </p>
      <h2 id="designs" className="mt-20">
        Avoiding horizontal gene transfer : Dut/Ung mutant
      </h2>
      <hr class="dividergeneral mb-5"></hr>
      <h3 id="lbt" className="mb-5">
        The importance of LBP for the upcoming years
      </h3>
      <p>
              Many diseases result from intricate interactions between metabolic and immunological processes,
              presenting significant medical challenges. There is a growing recognition of the role of microbes in human health and disease.
              Evidence is accumulating that engineered microbes, a new class of live biotherapeutics, could target disease mechanisms effectively.
              Leveraging synthetic biology, nonpathogenic bacteria can be engineered to detect environmental signals, eliminate harmful substances,
              and deliver therapeutic agents. In this perspective, we discuss key factors to consider in the design and development of engineered
              live biotherapeutics to gain regulatory approval and patient acceptance.
        <a href="#reference1">
          <sup>1</sup>
        </a>
        
      </p>
      <h3 id="resistance" className="mt-10">
        Present dangers with bacteria
      </h3>
      <p className="mb-2">
        Over the past decade, antibiotic resistance genes (ARGs) have
        significantly heightened the microbial threats to human health. The
        evaluation of the relative risks associated with these ARGs is a complex
        task, as numerous genes can impart resistance. Factors such as the
        prevalence of ARGs, their potential for lateral transfer, and their
        ability to be expressed in pathogenic microorganisms all contribute to
        their importance. In some studies, they identified a total of 2,561 ARGs
        that collectively conferred resistance to 24 different classes of
        antibiotics. Approximately 23.78% of the identified ARGs indeed present
        a health risk, particularly those responsible for multidrug resistance.
        ARGs are a threat to both human and animal health.
        <a href="#reference2">
          <sup>2</sup>
        </a>
        
      </p>
      <p className="mb-2">
        Our final concern revolves around horizontal gene transfer (HGT).
        Indeed, even if our genetically modified bacteria perish, the
        possibility of gene transfer between microbiotal strains and our
        bacteria before their demise remains a concern. Such transfer, involving
        one of our plasmids, could enable other bacteria to bind cadmium,
        produce SpaC, and develop antibiotic resistance. In the case of SpaC,
        the presence of IPTG and theophylline would still be required, making
        this occurrence less likely. Regarding EC20, it not anticipated to
        confer a particular advantage to the bacteria. However, we utilize two
        different antibiotic in our plasmids : ampicillin kanamycin. The primary
        issue is the development of antibiotic resistance.
      </p>
      <h3 id="uplasmid" className="mt-10 mb-5">
        Uplasmid : a promising tool for LBP
      </h3>
      <p className="mb-2">To prevent HGT, we developped the Dut/Ung system.</p>
      <p className="mb-2"> In bacteria such as <i>Escherichia coli</i>, 
        dUTP is involved in the pathway for <i>de novo</i> synthesis 
        of dTTP. Since DNA polymerases is not perfect, it makes mistakes and uses dUTP 
        in place of dTTP, some incorporation of dUMP into the bacterial chromosome is 
        unavoidable.
        </p>
      <p className="mb-2">
        DUT and UNG are two important genes that are common to many species as
        they have important functions in the organisms. Mutated DUT is responsible of 
        enhancing incorporation rate of uracil into dsDNA and UNG has a function to cut 
        heavily rich Uracilated DNA. These properties are important for us as we
        can create what we have coined <i>Uplasmid</i> which enable correct expression
        of the genes like in a classical plasmid but having gained the property
        to prevent its exchange with surrounding organisms.
      </p>
      <p className="mb-2">
        In organisms with double mutations, the DNA contains a significantly
        higher level of uracil. Surprisingly, these cells are able to survive,
        likely because most of the uracil units in this context actually
        function as substitutes for thymine. This substitution maintains the
        same coding characteristics as the Watson-Crick base pairing.
      </p>
      <p className="mb-2">
        As explained on the <a href="../epfl/engineering"> engineering page</a>,
        we devised an innovative method to produce the so coined term{" "}
        <i> Uplasmid </i> through a bacterial strain named CJ236 that has both
        mutations.
      </p>
      <p>
        <center>
          <details>
            <summary id="primary">
              <mark>
                <b> DUT mutation </b>
              </mark>
            </summary>
            <p> Codes for dUTP pyrophosphatase. <br></br>
                CJ236 dut mutation consists of a single nucleotide change, a C→T transition mutation at position 
                71 of E. coli dut coding sequence which generates a Thr→Ile amino acid change at codon 24. <a href="#reference3">
                <sup>3</sup></a>
                <br></br>
                From the NIH website {" "}
                <a href="https://www.ncbi.nlm.nih.gov/nuccore/NZ_CP029238.1?report=graph">
                    {" "}
                    Complete CJ236 Genome {" "}
                </a>{" "}
            </p>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/hgt-results/dut-cj236.pdf"
              width="800"
              height="500"
              aria-label="Primary Culture"
            ></object>
          </details>
        </center>
      </p>
      <br></br>
      <p>
        <center>
          <details>
            <summary id="primary">
              <mark>
                <b> UNG mutation </b>
              </mark>
            </summary>
            <p> 
                Codes for uracil-DNA glycosylase  <a href="#reference3">
                <sup>3</sup></a><br></br>
                From the NIH website {" "}
                <a href="https://www.ncbi.nlm.nih.gov/nuccore/NZ_CP029238.1?report=graph">
                    {" "}
                    Complete CJ236 Genome {" "}
                </a>{" "}
            </p>
            <object
              data="https://static.igem.wiki/teams/4888/wiki/hgt-results/ung-cj236.pdf"
              width="800"
              height="500"
              aria-label="Primary Culture"
            ></object>
          </details>
        </center>
      </p>
      <h3 id="benefits" className="mt-10 mb-5">
        Uplasmid : benefits and testing
      </h3>
      <p className="mb-2">
        We showed that Uplasmids have the ability to still express the protein
        it encodes while inserted into a strain that has at least the UNG gene
        mutation.
      </p>
      <p>
        Here are the results (see more on{" "}
        <a href="../epfl/results"> results page</a>).
      </p>
      <figure className="mt-10 mb-20">
        <img
          id="Figure1"
          src="https://static.igem.wiki/teams/4888/wiki/hgt-results/20-minutes-cj236.png"
          alt="project_description"
          class="centered-image"
          style={{ width: "90%" }}
        />
        <figcaption className="mt-5">
          <b>
            <u>Figure 1:</u>{" "}
          </b>
          histogram of viable colonies at 20 minutes of dUTP incubation.
        </figcaption>
      </figure>
      <figure className="mb-10">
        <img
          id="Figure1"
          src="https://static.igem.wiki/teams/4888/wiki/hgt-results/30-minutes-cj236.png"
          alt="project_description"
          class="centered-image"
          style={{ width: "90%" }}
        />
        <figcaption className="mt-5">
          <b>
            <u>Figure 2:</u>{" "}
          </b>
          histogram of viable colonies at at 30 minutes of dUTP incubation.
        </figcaption>
      </figure>
      <p>
        We have showed that we prevented more than 90% of the cases of HGT.
        dut+/ung+ transformed cells were mostly non viable on the plates.
      </p>
      <hr class="divider"></hr>
      <p>
        More information on the{" "}
        <a href="../epfl/engineering"> engineering page</a>.
      </p>
      <br />
    </div>
  );
};

export default TextBox;
